import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import {
  ArrowLeft, FileText, Eye, Save, Plus, Trash2, ChevronUp, ChevronDown,
  Download, ExternalLink, GripVertical,
} from "lucide-react";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { EventDocPDF } from "@/pdf/EventDocPDF";
import { DocumentSection } from "@/components/EventDocBlocks";

/**
 * Admin editor for the 5 Event Documents.
 * Structured section-based editor — each section has an ordered list of
 * blocks (paragraph, list, timeline, kv, callout, faq). Live preview
 * on the right, editor on the left. Published toggle + PDF download.
 */

const BLOCK_TYPES = [
  { value: "paragraph", label: "Paragraph" },
  { value: "list", label: "Bulleted List" },
  { value: "timeline", label: "Timeline" },
  { value: "kv", label: "Key / Value" },
  { value: "callout", label: "Callout" },
  { value: "faq", label: "FAQ" },
];

const newId = (p) => `${p}-${Math.random().toString(36).slice(2, 10)}`;

function emptyBlock(type) {
  const id = newId("blk");
  switch (type) {
    case "list":     return { id, type: "list", items: [""] };
    case "timeline": return { id, type: "timeline", items: [{ time: "", title: "", description: "" }] };
    case "kv":       return { id, type: "kv", pairs: [{ label: "", value: "" }] };
    case "callout":  return { id, type: "callout", tone: "soft", title: "", text: "" };
    case "faq":      return { id, type: "faq", items: [{ q: "", a: "" }] };
    default:         return { id, type: "paragraph", text: "" };
  }
}

export default function AdminDocumentsEditor() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [docs, setDocs] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [doc, setDoc] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user || (user.role !== "admin" && user.role !== "tl_internal")) {
      nav("/login"); return;
    }
    api.get("/admin/event-docs")
      .then(({ data }) => {
        setDocs(data.documents || []);
        if (data.documents?.length) setSelectedSlug(data.documents[0].slug);
      })
      .finally(() => setLoading(false));
  }, [user, authLoading, nav]);

  useEffect(() => {
    if (!selectedSlug) return;
    api.get(`/admin/event-docs/${selectedSlug}`)
      .then(({ data }) => { setDoc(data.document); setDirty(false); })
      .catch(() => toast.error("Failed to load document"));
  }, [selectedSlug]);

  const save = async () => {
    if (!doc) return;
    setSaving(true);
    try {
      const payload = {
        title: doc.title,
        subtitle: doc.subtitle,
        published: doc.published,
        sections: doc.sections,
      };
      const { data } = await api.put(`/admin/event-docs/${doc.slug}`, payload);
      setDoc(data.document);
      setDirty(false);
      // Refresh the docs list for updated published state
      const list = await api.get("/admin/event-docs");
      setDocs(list.data.documents || []);
      toast.success("Document saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const updateDoc = (patch) => { setDoc((d) => ({ ...d, ...patch })); setDirty(true); };
  const updateSection = (si, patch) => {
    setDoc((d) => {
      const next = { ...d, sections: d.sections.map((s, i) => i === si ? { ...s, ...patch } : s) };
      return next;
    });
    setDirty(true);
  };
  const moveSection = (si, dir) => {
    setDoc((d) => {
      const j = si + dir;
      if (j < 0 || j >= d.sections.length) return d;
      const arr = d.sections.slice();
      [arr[si], arr[j]] = [arr[j], arr[si]];
      return { ...d, sections: arr };
    });
    setDirty(true);
  };
  const addSection = () => {
    setDoc((d) => ({ ...d, sections: [...d.sections, { id: newId("sec"), heading: "New section", subheading: "", blocks: [] }] }));
    setDirty(true);
  };
  const removeSection = (si) => {
    if (!window.confirm("Delete this section?")) return;
    setDoc((d) => ({ ...d, sections: d.sections.filter((_, i) => i !== si) }));
    setDirty(true);
  };

  const updateBlock = (si, bi, patch) => {
    setDoc((d) => ({
      ...d,
      sections: d.sections.map((s, i) => i !== si ? s : ({
        ...s, blocks: s.blocks.map((b, j) => j !== bi ? b : ({ ...b, ...patch })),
      })),
    }));
    setDirty(true);
  };
  const moveBlock = (si, bi, dir) => {
    setDoc((d) => {
      const s = d.sections[si];
      const j = bi + dir;
      if (j < 0 || j >= s.blocks.length) return d;
      const arr = s.blocks.slice();
      [arr[bi], arr[j]] = [arr[j], arr[bi]];
      return {
        ...d,
        sections: d.sections.map((x, i) => i === si ? { ...x, blocks: arr } : x),
      };
    });
    setDirty(true);
  };
  const addBlock = (si, type) => {
    setDoc((d) => ({
      ...d,
      sections: d.sections.map((s, i) => i !== si ? s : ({
        ...s, blocks: [...s.blocks, emptyBlock(type)],
      })),
    }));
    setDirty(true);
  };
  const removeBlock = (si, bi) => {
    setDoc((d) => ({
      ...d,
      sections: d.sections.map((s, i) => i !== si ? s : ({
        ...s, blocks: s.blocks.filter((_, j) => j !== bi),
      })),
    }));
    setDirty(true);
  };

  if (authLoading || loading) {
    return <Layout><section className="container-summit py-24"><div className="overline">Loading…</div></section></Layout>;
  }

  return (
    <Layout>
      {/* Header */}
      <section className="bg-[#0A0A0A] text-white border-b border-white/10">
        <div className="container-summit py-8 md:py-10">
          <Link to="/admin" className="text-[11px] uppercase tracking-[0.22em] text-white/60 hover:text-white inline-flex items-center gap-2" data-testid="docs-back">
            <ArrowLeft size={12} /> All Workspaces
          </Link>
          <div className="mt-5 flex flex-wrap items-end justify-between gap-6">
            <div>
              <div className="overline text-white/60"><span className="accent-rule" />Content · Admin</div>
              <h1 className="serif text-4xl sm:text-5xl mt-3" data-testid="docs-editor-title">Event Documents</h1>
              <p className="mt-3 max-w-2xl text-white/70 text-sm">
                Five official documents published to every dashboard. Edit content, publish, and export as premium branded PDFs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="container-summit py-8">
        <div className="grid lg:grid-cols-[260px_1fr] gap-8">
          {/* Sidebar */}
          <aside className="lg:sticky lg:top-24 h-max">
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-3">Documents</div>
            <div className="grid gap-1" data-testid="docs-sidebar">
              {docs.map((d) => (
                <button key={d.slug}
                        onClick={() => {
                          if (dirty && !window.confirm("Discard unsaved changes?")) return;
                          setSelectedSlug(d.slug);
                        }}
                        className={`text-left px-3 py-3 border transition-colors ${selectedSlug === d.slug ? "border-black bg-black text-white" : "border-zinc-200 hover:border-black bg-white text-black"}`}
                        data-testid={`docs-select-${d.slug}`}>
                  <div className="flex items-center gap-2">
                    <FileText size={12} strokeWidth={1.4} />
                    <div className="text-xs uppercase tracking-[0.18em] font-medium truncate">{d.title}</div>
                  </div>
                  <div className={`text-[10px] mt-1 ${selectedSlug === d.slug ? "text-white/60" : "text-zinc-500"}`}>
                    {d.published ? "Published" : "Draft"} · {(d.sections || []).length} sections
                  </div>
                </button>
              ))}
            </div>
          </aside>

          {/* Editor */}
          <div>
            {!doc ? (
              <div className="text-sm text-zinc-500">Select a document.</div>
            ) : (
              <DocEditorPanel
                doc={doc}
                dirty={dirty}
                saving={saving}
                onSave={save}
                onDoc={updateDoc}
                onSection={updateSection}
                onSectionMove={moveSection}
                onSectionAdd={addSection}
                onSectionDelete={removeSection}
                onBlock={updateBlock}
                onBlockMove={moveBlock}
                onBlockAdd={addBlock}
                onBlockDelete={removeBlock}
              />
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
}

/* -------- Editor panel -------- */

function DocEditorPanel({
  doc, dirty, saving, onSave,
  onDoc, onSection, onSectionMove, onSectionAdd, onSectionDelete,
  onBlock, onBlockMove, onBlockAdd, onBlockDelete,
}) {
  const [tab, setTab] = useState("edit");

  return (
    <>
      {/* Top bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-200 pb-4 mb-6">
        <div className="flex items-center gap-2">
          <button onClick={() => setTab("edit")}
                  className={`text-[11px] uppercase tracking-[0.22em] px-3 py-2 border ${tab === "edit" ? "border-black bg-black text-white" : "border-zinc-200 text-zinc-600 hover:border-black"}`}
                  data-testid="docs-tab-edit">Edit</button>
          <button onClick={() => setTab("preview")}
                  className={`text-[11px] uppercase tracking-[0.22em] px-3 py-2 border ${tab === "preview" ? "border-black bg-black text-white" : "border-zinc-200 text-zinc-600 hover:border-black"}`}
                  data-testid="docs-tab-preview">Preview</button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-zinc-600" data-testid="docs-published-wrap">
            <input type="checkbox" checked={!!doc.published} onChange={(e) => onDoc({ published: e.target.checked })} data-testid="docs-published-toggle" />
            Published
          </label>
          <Link to={`/documents/${doc.slug}`} target="_blank" rel="noreferrer"
                className="btn-outline-black h-9 px-3 text-[11px] inline-flex items-center gap-2" data-testid="docs-open-public">
            <ExternalLink size={12} /> View
          </Link>
          <PDFDownloadLink
            document={<EventDocPDF doc={doc} />}
            fileName={`${doc.slug}-tl-supplier-summit-2026.pdf`}
            className="btn-outline-black h-9 px-3 text-[11px] inline-flex items-center gap-2" data-testid="docs-download-pdf">
            {({ loading: l }) => (<><Download size={12} /> {l ? "Preparing…" : "PDF"}</>)}
          </PDFDownloadLink>
          <button onClick={onSave} disabled={!dirty || saving}
                  className={`h-9 px-4 text-[11px] uppercase tracking-[0.22em] inline-flex items-center gap-2 border ${dirty ? "bg-[#A39171] border-[#A39171] text-white hover:bg-[#8f7f61]" : "border-zinc-200 text-zinc-400 cursor-not-allowed"}`}
                  data-testid="docs-save">
            <Save size={12} /> {saving ? "Saving…" : dirty ? "Save Changes" : "Saved"}
          </button>
        </div>
      </div>

      {tab === "edit" ? (
        <div className="space-y-6">
          {/* Title + subtitle */}
          <div className="border border-zinc-200 p-5">
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">Title</div>
            <input type="text" value={doc.title} onChange={(e) => onDoc({ title: e.target.value })}
                   className="w-full border-b border-zinc-300 py-2 text-2xl serif focus:border-black outline-none"
                   data-testid="docs-title-input" />
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mt-5 mb-2">Subtitle</div>
            <textarea value={doc.subtitle || ""} onChange={(e) => onDoc({ subtitle: e.target.value })}
                      rows={2}
                      className="w-full border border-zinc-200 p-3 text-sm resize-none focus:border-black outline-none"
                      data-testid="docs-subtitle-input" />
          </div>

          {/* Sections */}
          {(doc.sections || []).map((s, si) => (
            <SectionEditor key={s.id || si}
              section={s} index={si} total={doc.sections.length}
              onSection={(patch) => onSection(si, patch)}
              onMove={(dir) => onSectionMove(si, dir)}
              onDelete={() => onSectionDelete(si)}
              onBlock={(bi, patch) => onBlock(si, bi, patch)}
              onBlockMove={(bi, dir) => onBlockMove(si, bi, dir)}
              onBlockAdd={(type) => onBlockAdd(si, type)}
              onBlockDelete={(bi) => onBlockDelete(si, bi)}
            />
          ))}
          <button onClick={onSectionAdd}
                  className="w-full border-2 border-dashed border-zinc-300 py-5 text-[11px] uppercase tracking-[0.22em] text-zinc-600 hover:border-black hover:text-black inline-flex items-center justify-center gap-2"
                  data-testid="docs-add-section">
            <Plus size={14} /> Add section
          </button>
        </div>
      ) : (
        <div className="border border-zinc-200 p-8 md:p-12 bg-white">
          <div className="overline"><span className="accent-rule" />Preview</div>
          <h1 className="serif text-4xl md:text-5xl mt-4">{doc.title}</h1>
          {doc.subtitle && <p className="mt-4 text-zinc-500 leading-relaxed">{doc.subtitle}</p>}
          <div className="mt-10 space-y-12">
            {(doc.sections || []).map((s) => <DocumentSection key={s.id} section={s} />)}
          </div>
        </div>
      )}
    </>
  );
}

/* -------- Section editor -------- */

function SectionEditor({
  section, index, total,
  onSection, onMove, onDelete,
  onBlock, onBlockMove, onBlockAdd, onBlockDelete,
}) {
  const [addOpen, setAddOpen] = useState(false);
  return (
    <div className="border border-black bg-white" data-testid={`docs-section-${index}`}>
      <div className="flex items-center justify-between bg-black text-white px-4 py-2">
        <div className="flex items-center gap-2">
          <GripVertical size={12} className="opacity-50" />
          <span className="text-[10px] uppercase tracking-[0.22em]">Section {index + 1}</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => onMove(-1)} disabled={index === 0}
                  className="p-1.5 hover:bg-white/10 disabled:opacity-30" title="Move up" data-testid={`docs-section-up-${index}`}>
            <ChevronUp size={13} />
          </button>
          <button onClick={() => onMove(1)} disabled={index === total - 1}
                  className="p-1.5 hover:bg-white/10 disabled:opacity-30" title="Move down" data-testid={`docs-section-down-${index}`}>
            <ChevronDown size={13} />
          </button>
          <button onClick={onDelete}
                  className="p-1.5 hover:bg-red-500/70" title="Delete section" data-testid={`docs-section-delete-${index}`}>
            <Trash2 size={13} />
          </button>
        </div>
      </div>
      <div className="p-5 space-y-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-1.5">Heading</div>
          <input type="text" value={section.heading} onChange={(e) => onSection({ heading: e.target.value })}
                 className="w-full border-b border-zinc-300 py-1.5 serif text-xl focus:border-black outline-none"
                 data-testid={`docs-section-heading-${index}`} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-1.5">Subheading (optional)</div>
          <input type="text" value={section.subheading || ""} onChange={(e) => onSection({ subheading: e.target.value })}
                 className="w-full border-b border-zinc-300 py-1.5 text-sm focus:border-black outline-none"
                 data-testid={`docs-section-sub-${index}`} />
        </div>

        {/* Blocks */}
        <div className="mt-4 space-y-3">
          {(section.blocks || []).map((b, bi) => (
            <BlockEditor key={b.id || bi}
              block={b} index={bi} total={section.blocks.length}
              onChange={(patch) => onBlock(bi, patch)}
              onMove={(dir) => onBlockMove(bi, dir)}
              onDelete={() => onBlockDelete(bi)}
              sectionIndex={index}
            />
          ))}
        </div>

        {/* Add block */}
        <div className="border-t border-zinc-100 pt-4">
          {!addOpen ? (
            <button onClick={() => setAddOpen(true)}
                    className="text-[11px] uppercase tracking-[0.22em] text-zinc-600 hover:text-black inline-flex items-center gap-2"
                    data-testid={`docs-add-block-open-${index}`}>
              <Plus size={12} /> Add block
            </button>
          ) : (
            <div className="flex flex-wrap gap-2">
              {BLOCK_TYPES.map((t) => (
                <button key={t.value} onClick={() => { onBlockAdd(t.value); setAddOpen(false); }}
                        className="text-[11px] uppercase tracking-[0.22em] border border-zinc-200 px-3 py-2 hover:border-black hover:bg-black hover:text-white"
                        data-testid={`docs-add-block-${index}-${t.value}`}>
                  {t.label}
                </button>
              ))}
              <button onClick={() => setAddOpen(false)}
                      className="text-[11px] uppercase tracking-[0.22em] text-zinc-400 px-3 py-2 hover:text-black">
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* -------- Block editor (per type) -------- */

function BlockShell({ children, label, index, total, onMove, onDelete, testId }) {
  return (
    <div className="border border-zinc-200 bg-white" data-testid={testId}>
      <div className="flex items-center justify-between border-b border-zinc-100 px-3 py-1.5 bg-zinc-50">
        <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">{label}</div>
        <div className="flex items-center gap-1">
          <button onClick={() => onMove(-1)} disabled={index === 0}
                  className="p-1 hover:bg-zinc-200 disabled:opacity-30" title="Move up"><ChevronUp size={12} /></button>
          <button onClick={() => onMove(1)} disabled={index === total - 1}
                  className="p-1 hover:bg-zinc-200 disabled:opacity-30" title="Move down"><ChevronDown size={12} /></button>
          <button onClick={onDelete}
                  className="p-1 hover:bg-red-100 text-zinc-500 hover:text-red-700" title="Delete"><Trash2 size={12} /></button>
        </div>
      </div>
      <div className="p-3">{children}</div>
    </div>
  );
}

function BlockEditor({ block, index, total, onChange, onMove, onDelete, sectionIndex }) {
  const t = block.type;
  const testId = `docs-block-${sectionIndex}-${index}`;
  const labelMap = { paragraph: "Paragraph", list: "List", timeline: "Timeline", kv: "Key / Value", callout: "Callout", faq: "FAQ" };

  if (t === "paragraph") {
    return (
      <BlockShell label="Paragraph" index={index} total={total} onMove={onMove} onDelete={onDelete} testId={testId}>
        <textarea value={block.text || ""} onChange={(e) => onChange({ text: e.target.value })}
                  rows={4} className="w-full border border-zinc-200 p-2 text-sm focus:border-black outline-none"
                  data-testid={`${testId}-text`} />
      </BlockShell>
    );
  }
  if (t === "list") {
    const items = block.items || [];
    return (
      <BlockShell label="Bulleted List" index={index} total={total} onMove={onMove} onDelete={onDelete} testId={testId}>
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={i} className="flex gap-2">
              <span className="text-[#A39171] pt-1.5">—</span>
              <input type="text" value={it}
                     onChange={(e) => onChange({ items: items.map((x, j) => j === i ? e.target.value : x) })}
                     className="flex-1 border-b border-zinc-200 py-1 text-sm focus:border-black outline-none"
                     data-testid={`${testId}-item-${i}`} />
              <button onClick={() => onChange({ items: items.filter((_, j) => j !== i) })}
                      className="text-zinc-400 hover:text-red-600 px-1"><Trash2 size={12} /></button>
            </div>
          ))}
          <button onClick={() => onChange({ items: [...items, ""] })}
                  className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 hover:text-black inline-flex items-center gap-1 mt-2"
                  data-testid={`${testId}-add-item`}>
            <Plus size={11} /> Add item
          </button>
        </div>
      </BlockShell>
    );
  }
  if (t === "timeline") {
    const items = block.items || [];
    return (
      <BlockShell label="Timeline" index={index} total={total} onMove={onMove} onDelete={onDelete} testId={testId}>
        <div className="space-y-3">
          {items.map((it, i) => (
            <div key={i} className="grid grid-cols-[120px_1fr_auto] gap-2 items-start">
              <input type="text" value={it.time || ""} placeholder="9:00 AM"
                     onChange={(e) => onChange({ items: items.map((x, j) => j === i ? { ...x, time: e.target.value } : x) })}
                     className="border border-zinc-200 p-2 text-xs focus:border-black outline-none"
                     data-testid={`${testId}-time-${i}`} />
              <div className="space-y-1.5">
                <input type="text" value={it.title || ""} placeholder="Title"
                       onChange={(e) => onChange({ items: items.map((x, j) => j === i ? { ...x, title: e.target.value } : x) })}
                       className="w-full border border-zinc-200 p-2 text-sm focus:border-black outline-none"
                       data-testid={`${testId}-title-${i}`} />
                <textarea value={it.description || ""} placeholder="Description (optional)" rows={2}
                          onChange={(e) => onChange({ items: items.map((x, j) => j === i ? { ...x, description: e.target.value } : x) })}
                          className="w-full border border-zinc-200 p-2 text-xs focus:border-black outline-none resize-none"
                          data-testid={`${testId}-desc-${i}`} />
              </div>
              <button onClick={() => onChange({ items: items.filter((_, j) => j !== i) })}
                      className="text-zinc-400 hover:text-red-600 px-1 pt-1"><Trash2 size={12} /></button>
            </div>
          ))}
          <button onClick={() => onChange({ items: [...items, { time: "", title: "", description: "" }] })}
                  className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 hover:text-black inline-flex items-center gap-1"
                  data-testid={`${testId}-add-item`}>
            <Plus size={11} /> Add row
          </button>
        </div>
      </BlockShell>
    );
  }
  if (t === "kv") {
    const pairs = block.pairs || [];
    return (
      <BlockShell label="Key / Value" index={index} total={total} onMove={onMove} onDelete={onDelete} testId={testId}>
        <div className="space-y-2">
          {pairs.map((p, i) => (
            <div key={i} className="grid grid-cols-[140px_1fr_auto] gap-2">
              <input type="text" value={p.label || ""} placeholder="Label"
                     onChange={(e) => onChange({ pairs: pairs.map((x, j) => j === i ? { ...x, label: e.target.value } : x) })}
                     className="border border-zinc-200 p-2 text-xs uppercase tracking-[0.14em] focus:border-black outline-none"
                     data-testid={`${testId}-label-${i}`} />
              <input type="text" value={p.value || ""} placeholder="Value"
                     onChange={(e) => onChange({ pairs: pairs.map((x, j) => j === i ? { ...x, value: e.target.value } : x) })}
                     className="border border-zinc-200 p-2 text-sm focus:border-black outline-none"
                     data-testid={`${testId}-value-${i}`} />
              <button onClick={() => onChange({ pairs: pairs.filter((_, j) => j !== i) })}
                      className="text-zinc-400 hover:text-red-600 px-1"><Trash2 size={12} /></button>
            </div>
          ))}
          <button onClick={() => onChange({ pairs: [...pairs, { label: "", value: "" }] })}
                  className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 hover:text-black inline-flex items-center gap-1"
                  data-testid={`${testId}-add-item`}>
            <Plus size={11} /> Add pair
          </button>
        </div>
      </BlockShell>
    );
  }
  if (t === "callout") {
    return (
      <BlockShell label="Callout" index={index} total={total} onMove={onMove} onDelete={onDelete} testId={testId}>
        <div className="grid gap-2">
          <div className="flex gap-2">
            {["gold", "black", "soft"].map((tone) => (
              <button key={tone} onClick={() => onChange({ tone })}
                      className={`text-[11px] uppercase tracking-[0.22em] px-3 py-1.5 border ${block.tone === tone ? "border-black bg-black text-white" : "border-zinc-200 hover:border-black"}`}
                      data-testid={`${testId}-tone-${tone}`}>
                {tone}
              </button>
            ))}
          </div>
          <input type="text" value={block.title || ""} placeholder="Title (optional)"
                 onChange={(e) => onChange({ title: e.target.value })}
                 className="border border-zinc-200 p-2 text-xs uppercase tracking-[0.14em] focus:border-black outline-none"
                 data-testid={`${testId}-title`} />
          <textarea value={block.text || ""} rows={3} placeholder="Callout text"
                    onChange={(e) => onChange({ text: e.target.value })}
                    className="border border-zinc-200 p-2 text-sm focus:border-black outline-none"
                    data-testid={`${testId}-text`} />
        </div>
      </BlockShell>
    );
  }
  if (t === "faq") {
    const items = block.items || [];
    return (
      <BlockShell label="FAQ" index={index} total={total} onMove={onMove} onDelete={onDelete} testId={testId}>
        <div className="space-y-3">
          {items.map((it, i) => (
            <div key={i} className="grid grid-cols-[1fr_auto] gap-2">
              <div className="space-y-1.5">
                <input type="text" value={it.q || ""} placeholder="Question"
                       onChange={(e) => onChange({ items: items.map((x, j) => j === i ? { ...x, q: e.target.value } : x) })}
                       className="w-full border border-zinc-200 p-2 text-sm font-medium focus:border-black outline-none"
                       data-testid={`${testId}-q-${i}`} />
                <textarea value={it.a || ""} placeholder="Answer" rows={3}
                          onChange={(e) => onChange({ items: items.map((x, j) => j === i ? { ...x, a: e.target.value } : x) })}
                          className="w-full border border-zinc-200 p-2 text-sm focus:border-black outline-none resize-none"
                          data-testid={`${testId}-a-${i}`} />
              </div>
              <button onClick={() => onChange({ items: items.filter((_, j) => j !== i) })}
                      className="text-zinc-400 hover:text-red-600 px-1 pt-1"><Trash2 size={12} /></button>
            </div>
          ))}
          <button onClick={() => onChange({ items: [...items, { q: "", a: "" }] })}
                  className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 hover:text-black inline-flex items-center gap-1"
                  data-testid={`${testId}-add-item`}>
            <Plus size={11} /> Add Q&amp;A
          </button>
        </div>
      </BlockShell>
    );
  }
  return null;
}
